import React, { useEffect, useState } from 'react';
import { XIcon, TagIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyCoupon: (code: string) => void;
}
export function CouponModal({
  isOpen,
  onClose,
  onApplyCoupon
}: CouponModalProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const coupons = [{
    code: 'SAVE20',
    title: '20% Off Your Order',
    description: 'Save 20% on orders above SAR 200',
    minOrder: 200,
    discount: '20%',
    expiryDate: '2024-12-31'
  }, {
    code: 'WELCOME10',
    title: 'Welcome Offer',
    description: 'Get 10% off on your first order',
    minOrder: 100,
    discount: '10%',
    expiryDate: '2024-12-31'
  }, {
    code: 'FREESHIP',
    title: 'Free Shipping',
    description: 'Free delivery on orders above SAR 150',
    minOrder: 150,
    discount: 'Free Shipping',
    expiryDate: '2024-12-31'
  }, {
    code: 'HEALTH50',
    title: 'SAR 50 Off',
    description: 'Get SAR 50 off on orders above SAR 300',
    minOrder: 300,
    discount: 'SAR 50',
    expiryDate: '2024-12-31'
  }];
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const handleApplyCoupon = (code: string) => {
    onApplyCoupon(code);
    onClose();
  };
  return <AnimatePresence data-id="element-3986" data-mp="lmqj426snnrm">
      {isOpen && <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" data-id="element-3987" data-mp="lq4jx5zt7owy">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm" data-id="element-3988" data-mp="2mprc7af4pje1" />

          {/* Modal Content */}
          <motion.div initial={isMobile ? {
        y: '100%'
      } : {
        opacity: 0,
        scale: 0.95
      }} animate={isMobile ? {
        y: 0
      } : {
        opacity: 1,
        scale: 1
      }} exit={isMobile ? {
        y: '100%'
      } : {
        opacity: 0,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative bg-white w-full max-w-2xl overflow-hidden shadow-2xl ${isMobile ? 'rounded-t-3xl max-h-[85vh]' : 'rounded-2xl max-h-[90vh] mx-4'}`} data-id="element-3989" data-mp="2qs4fls6el57n">
            {/* Mobile Handle */}
            {isMobile && <div className="flex justify-center pt-3 pb-1" data-id="element-3990" data-mp="ejphigfx3kc4">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3991" data-mp="ijptzvjghlgj" />
              </div>}

            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10" data-id="element-3992" data-mp="4kq6ir7ojcly">
              <div className="flex items-center gap-3" data-id="element-3993" data-mp="9i8f2ghsbptf">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-3994" data-mp="2pafkz34h0we4">
                  <TagIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-3995" data-mp="2u9inaft2goie" />
                </div>
                <div data-id="element-3996" data-mp="2u7611qg9zrzj">
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-3997" data-mp="39ixcwsdkey1n">
                    Available Coupons
                  </h2>
                  <p className="text-xs text-gray-500" data-id="element-3998" data-mp="34re3ilxkejsi">
                    Apply code to save more
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3999" data-mp="2vs0utvh9lz8u">
                <XIcon className="w-5 h-5" data-id="element-4000" data-mp="3ogg8adkr09i2" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] md:max-h-[calc(90vh-80px)] space-y-4" data-id="element-4001" data-mp="3oebrww5nxi0w">
              {coupons.map((coupon, index) => <div key={index} className="group border border-gray-200 rounded-xl p-4 hover:border-[#1E2B55] hover:bg-blue-50/30 transition-all duration-200" data-id="element-4002" data-mp="3eh78v57462ob" data-mpi={index}>
                  <div className="flex items-start justify-between mb-4" data-id="element-4003" data-mp="231ijy8aautkv">
                    <div className="flex-1" data-id="element-4004" data-mp="1t07tkv9d2gdb">
                      <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#1E2B55] transition-colors" data-id="element-4005" data-mp="1sx26flkalok8">
                        {coupon.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-4006" data-mp="22vrk1255omuo">
                        {coupon.description}
                      </p>
                      <div className="flex items-center gap-3 text-[10px] sm:text-xs text-gray-500 font-medium" data-id="element-4007" data-mp="2bpo66gyblza3">
                        <span className="bg-gray-100 px-2 py-1 rounded" data-id="element-4008" data-mp="2go7uup4atp65">
                          Min Order: SAR {coupon.minOrder}
                        </span>
                        <span className="bg-gray-100 px-2 py-1 rounded" data-id="element-4009" data-mp="2llr6dh9m3nue">
                          Valid till: {coupon.expiryDate}
                        </span>
                      </div>
                    </div>
                    <div className="bg-green-100 text-green-700 font-bold px-3 py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap ml-4 shadow-sm" data-id="element-4010" data-mp="rc5m4cfha2l2">
                      {coupon.discount}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-4011" data-mp="ja6high8c110">
                    <div className="flex-1 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-4 py-2.5 font-mono text-sm font-bold text-[#1E2B55] flex items-center justify-between" data-id="element-4012" data-mp="y7fcdk5qrmxh">
                      {coupon.code}
                      <button onClick={() => handleCopyCode(coupon.code)} className="text-gray-400 hover:text-[#1E2B55] transition-colors" title="Copy Code" data-id="element-4013" data-mp="175974qnuy8ij">
                        {copiedCode === coupon.code ? <CheckIcon className="w-4 h-4 text-green-600" data-id="element-4014" data-mp="1bzviittxw12m" /> : <CopyIcon className="w-4 h-4" data-id="element-4015" data-mp="1pvh1okdhrqn4" />}
                      </button>
                    </div>
                    <button onClick={() => handleApplyCoupon(coupon.code)} className="bg-[#1E2B55] hover:bg-[#162041] text-white px-6 py-2.5 rounded-lg transition-all text-sm font-bold shadow-md hover:shadow-lg active:scale-95" data-id="element-4016" data-mp="24jquk8ibngps">
                      Apply Coupon
                    </button>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}